import api from '../services/api';

/**
 * Shared helpers for document templates (attestations, payslip).
 *
 * A dynamic field is stored in the template HTML as a "chip":
 *   <span class="doc-var" data-var="fullName">Nom & Prénom</span>
 * The admin never types a code: fields are picked from a list and shown by name.
 * The legacy `{fullName}` notation is converted to chips when a template is opened.
 */

export const DOC_LANGUAGES = [
  { value: 'fr', label: 'Français', short: 'FR' },
  { value: 'en', label: 'English', short: 'EN' },
];

export const normalizeLanguage = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en' : 'fr');

// ── Field catalog ────────────────────────────────────────────────

const f = (key, fr, en) => ({ key, fr, en });

export const FIELD_GROUPS = [
  {
    id: 'employee',
    fr: 'Employé',
    en: 'Employee',
    fields: [
      f('fullName', 'Nom & Prénom', 'Full name'),
      f('firstName', 'Prénom', 'First name'),
      f('lastName', 'Nom', 'Last name'),
      f('cin', 'N° CIN', 'National ID (CIN)'),
      f('dateOfBirth', 'Date de naissance', 'Date of birth'),
      f('matricule', 'Matricule', 'Employee ID'),
      f('cnss', 'N° CNSS', 'CNSS number'),
      f('email', 'Email', 'Email'),
      f('phoneNumber', 'Téléphone', 'Phone'),
    ],
  },
  {
    id: 'job',
    fr: 'Poste & contrat',
    en: 'Position & contract',
    fields: [
      f('position', 'Poste / Fonction', 'Position'),
      f('department', 'Département', 'Department'),
      f('contractType', 'Type de contrat', 'Contract type'),
      f('hireDate', "Date d'embauche", 'Date of joining'),
      f('salary', 'Salaire', 'Salary'),
      f('role', 'Rôle', 'Role'),
    ],
  },
  {
    id: 'internship',
    fr: 'Stage',
    en: 'Internship',
    fields: [
      f('academicInstitution', 'Établissement', 'Academic institution'),
      f('internshipProject', 'Projet de stage', 'Internship project'),
      f('internshipStartDate', 'Début du stage', 'Internship start'),
      f('internshipEndDate', 'Fin du stage', 'Internship end'),
      f('supervisorName', 'Encadrant', 'Supervisor'),
    ],
  },
  {
    id: 'request',
    fr: 'Demande',
    en: 'Request',
    fields: [
      f('purpose', 'Motif de la demande', 'Purpose'),
      f('startDate', 'Date de début', 'Start date'),
      f('endDate', 'Date de fin', 'End date'),
      f('additionalNotes', 'Notes', 'Notes'),
      f('attestationType', 'Type de document', 'Document type'),
      f('reference', 'Référence', 'Reference'),
      f('version', 'Version', 'Version'),
    ],
  },
  {
    id: 'general',
    fr: 'Date & société',
    en: 'Date & company',
    fields: [
      f('date', 'Date du jour', "Today's date"),
      f('generatedDateFull', 'Date du jour (en lettres)', "Today's date (long)"),
      f('year', 'Année', 'Year'),
      f('companyName', 'Nom de la société', 'Company name'),
    ],
  },
  {
    id: 'payroll',
    payslipOnly: true,
    fr: 'Paie',
    en: 'Payroll',
    fields: [
      f('month', 'Mois de paie', 'Payroll month'),
      f('hourlyRate', 'Taux horaire', 'Hourly rate'),
      f('regularHours', 'Heures normales', 'Regular hours'),
      f('overtimeHours', 'Heures supplémentaires', 'Overtime hours'),
      f('grossSalary', 'Salaire brut', 'Gross salary'),
      f('cnssDeduction', 'Retenue CNSS', 'CNSS deduction'),
      f('irppTax', 'IRPP', 'Income tax (IRPP)'),
      f('netSalary', 'Net à payer', 'Net salary'),
      f('paymentMethod', 'Mode de paiement', 'Payment method'),
      f('defaultBank', 'Banque', 'Bank'),
      f('cnssEmployerNumber', 'N° CNSS employeur', 'Employer CNSS number'),
    ],
  },
];

// Older templates use these names for the same information
const ALIAS_FIELDS = [
  f('departmentName', 'Département', 'Department'),
  f('jobTitle', 'Poste / Fonction', 'Position'),
  f('issueDate', 'Date de délivrance', 'Issue date'),
  f('generatedDate', 'Date de génération', 'Generated date'),
  f('username', "Nom d'utilisateur", 'Username'),
  f('notes', 'Notes RH', 'HR notes'),
];

const FIELD_INDEX = new Map(
  [...FIELD_GROUPS.flatMap((g) => g.fields), ...ALIAS_FIELDS].map((field) => [field.key.toLowerCase(), field]),
);

const prettifyKey = (key) => String(key).replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/^\w/, (l) => l.toUpperCase());

/** Friendly name of a field in the given language (custom fields carry their own label). */
export const fieldLabel = (key, lang = 'fr', customFields = []) => {
  const known = FIELD_INDEX.get(String(key).toLowerCase());
  if (known) return known[lang] || known.fr;
  const custom = customFields.find((c) => c.key === key);
  return custom?.label || prettifyKey(key);
};

export const isSystemField = (key) => FIELD_INDEX.has(String(key).toLowerCase());

/** Turn a human label into a stable field key ("Nom du tuteur" → "nomDuTuteur"). */
export const labelToKey = (label) => {
  const words = String(label || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  if (words.length === 0) return '';
  const key = words.map((w, i) => (i === 0 ? w.toLowerCase() : w[0].toUpperCase() + w.slice(1).toLowerCase())).join('');
  return /^\d/.test(key) ? `champ${key}` : key;
};

// ── Chips ────────────────────────────────────────────────────────

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const chipHtml = (key, label) =>
  `<span class="doc-var" data-var="${escapeHtml(key)}" contenteditable="false">${escapeHtml(label)}</span>`;

const BRACE_RE = /\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}|\{\s*([a-zA-Z0-9_-]+)\s*\}/g;

/**
 * Prepare stored HTML for the editor: legacy `{key}` text becomes chips and
 * every chip is made non-editable. Returns the HTML and the keys it uses.
 */
export const toEditorHtml = (html, labelOf) => {
  const root = document.createElement('div');
  root.innerHTML = html || '';

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!node.parentElement?.closest('.doc-var, style, script') && /\{/.test(node.nodeValue)) textNodes.push(node);
  }
  textNodes.forEach((node) => {
    const text = node.nodeValue;
    const re = new RegExp(BRACE_RE.source, 'g');
    if (!re.test(text)) return;
    re.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let last = 0;
    let match;
    while ((match = re.exec(text))) {
      if (match.index > last) fragment.appendChild(document.createTextNode(text.slice(last, match.index)));
      const key = match[1] || match[2];
      const chip = document.createElement('span');
      chip.className = 'doc-var';
      chip.dataset.var = key;
      chip.textContent = labelOf(key);
      fragment.appendChild(chip);
      last = match.index + match[0].length;
    }
    if (last < text.length) fragment.appendChild(document.createTextNode(text.slice(last)));
    node.parentNode.replaceChild(fragment, node);
  });

  const keys = [];
  root.querySelectorAll('.doc-var[data-var]').forEach((chip) => {
    chip.setAttribute('contenteditable', 'false');
    chip.className = 'doc-var';
    if (!keys.includes(chip.dataset.var)) keys.push(chip.dataset.var);
  });
  return { html: root.innerHTML, keys };
};

/** Keys of the fields present in a DOM subtree or an HTML string, in document order. */
export const usedFieldKeys = (source) => {
  let root = source;
  if (typeof source === 'string') {
    root = document.createElement('div');
    root.innerHTML = source;
  }
  const keys = [];
  root?.querySelectorAll?.('.doc-var[data-var]').forEach((chip) => {
    if (!keys.includes(chip.dataset.var)) keys.push(chip.dataset.var);
  });
  return keys;
};

/** Show each chip as its field name (template) or as its value (generation). */
export const decorateChips = (root, { labelOf, values = null }) => {
  if (!root) return;
  root.querySelectorAll('.doc-var[data-var]').forEach((chip) => {
    const key = chip.dataset.var;
    const label = labelOf(key);
    chip.title = label;
    chip.setAttribute('contenteditable', 'false');
    if (values) {
      const value = String(values[key] ?? '').trim();
      chip.textContent = value || label;
      chip.classList.toggle('is-empty', !value);
      chip.classList.add('is-value');
    } else {
      chip.textContent = label;
      chip.classList.remove('is-empty', 'is-value');
    }
  });
};

/** Canonical HTML saved for a template: chips carry the field name, no editor state. */
export const toStoredHtml = (root, labelOf) => {
  const clone = root.cloneNode(true);
  clone.querySelectorAll('.doc-var[data-var]').forEach((chip) => {
    chip.className = 'doc-var';
    chip.removeAttribute('title');
    chip.removeAttribute('contenteditable');
    chip.textContent = labelOf(chip.dataset.var);
  });
  return clone.innerHTML;
};

/** Final document: every chip replaced by plain text. */
export const fillHtml = (source, values = {}) => {
  let root = source;
  if (typeof source === 'string') {
    root = document.createElement('div');
    root.innerHTML = source;
  } else {
    root = source.cloneNode(true);
  }
  root.querySelectorAll('.doc-var[data-var]').forEach((chip) => {
    chip.replaceWith(document.createTextNode(String(values[chip.dataset.var] ?? '')));
  });
  return root.innerHTML;
};

// ── Page geometry ────────────────────────────────────────────────

export const DEFAULT_PAGE_SETTINGS = {
  widthMm: 210,
  heightMm: 297,
  marginTopMm: 20,
  marginRightMm: 20,
  marginBottomMm: 20,
  marginLeftMm: 20,
  pages: 1,
  fontFamily: 'Times New Roman',
  fontSizePt: 12,
  lineHeight: 1.5,
  paragraphSpacingPt: 8,
  headerMm: 12.5,
  footerMm: 12.5,
};

export const PAGE_FORMATS = [
  { id: 'a4', label: 'A4 (210 × 297 mm)', widthMm: 210, heightMm: 297 },
  { id: 'letter', label: 'Letter (216 × 279 mm)', widthMm: 215.9, heightMm: 279.4 },
  { id: 'a5', label: 'A5 (148 × 210 mm)', widthMm: 148, heightMm: 210 },
];

export const pageFormatLabel = (page) => {
  const near = (a, b) => Math.abs(a - b) < 1.5;
  const match = PAGE_FORMATS.find((fmt) => (near(fmt.widthMm, page.widthMm) && near(fmt.heightMm, page.heightMm))
    || (near(fmt.widthMm, page.heightMm) && near(fmt.heightMm, page.widthMm)));
  const size = `${Math.round(page.widthMm)} × ${Math.round(page.heightMm)} mm`;
  return match ? `${match.id.toUpperCase()} · ${size}` : size;
};

const PX_PER_MM = 96 / 25.4;
export const mmToPx = (mm) => mm * PX_PER_MM;

/**
 * Base styles of a document page. Kept in sync with `documentPageCss` in
 * src/attestations/document-render.ts so the editor shows what is printed.
 * The page header and footer of a Word file sit in the top and bottom margins.
 */
export const documentPageCss = (page) => `
.doc-page { font-family: '${page.fontFamily}', 'Times New Roman', serif; font-size: ${page.fontSizePt}pt; line-height: ${page.lineHeight || 1.5}; color: #000; overflow-wrap: break-word; font-kerning: none; font-variant-ligatures: none; }
.doc-page > div:only-child { max-width: none !important; margin: 0 !important; padding: 0 !important; border: none !important; box-shadow: none !important; border-radius: 0 !important; background: transparent !important; }
.doc-page p { margin: 0 0 ${page.paragraphSpacingPt ?? 8}pt; }
.doc-page h1 { font-size: 1.6em; margin: 0 0 10pt; }
.doc-page h2 { font-size: 1.3em; margin: 0 0 8pt; }
.doc-page h3 { font-size: 1.1em; margin: 0 0 6pt; }
.doc-page ul, .doc-page ol { margin: 0 0 8pt; padding-left: 24pt; }
.doc-page img { max-width: 100%; height: auto; }
.doc-page table { border-collapse: collapse; max-width: 100%; }
.doc-page td, .doc-page th { vertical-align: top; }
.doc-page .docx-body-content { position: relative; z-index: 0; }
.doc-page .doc-header, .doc-page .doc-footer { position: absolute; left: ${page.marginLeftMm}mm; right: ${page.marginRightMm}mm; z-index: 0; }
.doc-page .doc-header { top: ${page.headerMm ?? 12.5}mm; }
.doc-page .doc-footer { bottom: ${page.footerMm ?? 12.5}mm; }
`;

/**
 * How the content relates to the pages it must fit on.
 * `ratio` > 1 means it overflows and will be scaled down when printed.
 */
export const measureFit = (contentEl, page) => {
  if (!contentEl) return { ratio: 0, overflow: false };
  const available = mmToPx(page.heightMm - page.marginTopMm - page.marginBottomMm) * page.pages;
  const ratio = available > 0 ? contentEl.scrollHeight / available : 0;
  return { ratio, overflow: ratio > 1.005 };
};

// ── Print / download ─────────────────────────────────────────────

/** Print a PDF blob as-is: the printed sheet is exactly the PDF page. */
export const printPdfBlob = (blob) => {
  const url = window.URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  frame.src = url;
  frame.onload = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch {
      // Some browsers refuse to script their PDF viewer: open it instead
      window.open(url, '_blank');
    }
  };
  document.body.appendChild(frame);
  // Keep the frame while the print dialog is open, then clean up
  setTimeout(() => {
    frame.remove();
    window.URL.revokeObjectURL(url);
  }, 5 * 60 * 1000);
};

/** Print the generated certificate of a request (always the PDF, never re-flowed HTML). */
export const printAttestation = async (requestId) => {
  const blob = await api.downloadBlob(`/attestations/${requestId}/download?format=pdf`);
  printPdfBlob(blob);
};
